import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  SupportAgent as SavIcon, ArrowBack as ArrowBackIcon,
  ReportProblem as AlertIcon, CheckCircle as ResolvedIcon,
  Schedule as PendingIcon, Store as ChantierIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmReclamation, ReclamationType, ReclamationSeverity, ReclamationStatus, CrmChantier, CrmContact } from '../types/crm';

const emptyReclamation = (): Partial<CrmReclamation> => ({
  subject: '',
  type: 'DEFAUT_POSE',
  severity: 'MEDIUM',
  status: 'OPEN',
  dateReported: new Date().toISOString().substring(0, 16),
  resolutionNotes: ''
});

const getSeverityColor = (sev: ReclamationSeverity): 'default' | 'info' | 'warning' | 'error' => {
  switch (sev) {
    case 'LOW': return 'info';
    case 'MEDIUM': return 'warning';
    case 'HIGH':
    case 'CRITICAL': return 'error';
    default: return 'default';
  }
};

const getStatusColor = (status: ReclamationStatus): 'default' | 'info' | 'warning' | 'success' => {
  switch (status) {
    case 'OPEN': return 'error' as any;
    case 'INVESTIGATING': return 'info';
    case 'IN_PROGRESS': return 'warning';
    case 'RESOLVED':
    case 'CLOSED': return 'success';
    default: return 'default';
  }
};

export const ReclamationsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [reclamationsList, setReclamationsList] = useState<CrmReclamation[]>([]);
  const [chantiers, setChantiers] = useState<CrmChantier[]>([]);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmReclamation>>(emptyReclamation());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rData, chData, ctData] = await Promise.all([
        crmApi.getReclamations(),
        crmApi.getChantiers(),
        crmApi.getContacts()
      ]);
      setReclamationsList(rData);
      setChantiers(chData);
      setContacts(ctData);
    } catch (err) {
      console.error('Error loading CRM reclamations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyReclamation());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (rec: CrmReclamation) => {
    setFormData({ ...rec });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (rec: CrmReclamation) => {
    const recId = (rec.id || rec.CRC_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.reclamations.confirmDelete', 'Êtes-vous sûr de vouloir supprimer cette réclamation SAV ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteReclamation(recId);
      if (res.success) {
        blobticMessage.success(t('crm.reclamations.deleteSuccess', 'Réclamation supprimée avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.subject?.trim()) {
      return t('crm.reclamations.subjectRequired', 'L\'objet de la réclamation est obligatoire.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CRC_IDF: formData.id || formData.CRC_IDF,
      CRC_CODE: formData.code || formData.CRC_CODE,
      CCH_IDF: formData.chantierId || formData.CCH_IDF,
      COP_IDF: formData.projectId || formData.COP_IDF,
      CCT_IDF: formData.contactId || formData.CCT_IDF,
      CRC_SUBJECT: formData.subject,
      CRC_TYPE: formData.type,
      CRC_SEVERITY: formData.severity,
      CRC_STATUS: formData.status,
      CRC_DATE_REPORTED: formData.dateReported,
      CRC_DATE_RESOLVED: formData.dateResolved,
      CRC_RESOLUTION_NOTES: formData.resolutionNotes,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.reclamations.editTitle', 'Traiter la Réclamation SAV') : t('crm.reclamations.newTitle', 'Déclarer une Réclamation SAV')}
        icon={<SavIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_reclamation', primaryKey: 'CRC_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Déclaration */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlertIcon color="error" /> {t('crm.reclamations.secIdent', 'Détails de l\'Anomalie Chantier')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('common.code', 'Code SAV')}
                      value={formData.code || t('common.autoGenerated', '(Automatique)')}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <TextField
                      label={t('crm.reclamations.subject', 'Objet / Description synthétique du litige')}
                      value={formData.subject || ''}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label={t('crm.reclamations.chantier', 'Chantier Concerné')}
                      value={formData.chantierId || ''}
                      onChange={(e) => setFormData({ ...formData, chantierId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">-- {t('crm.reclamations.selectChantier', 'Sélectionner un chantier')} --</MenuItem>
                      {chantiers.map((ch) => (
                        <MenuItem key={ch.id || ch.CCH_IDF} value={ch.id || ch.CCH_IDF}>
                          {ch.title} ({ch.clientName})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label={t('crm.reclamations.contact', 'Interlocuteur Déclarant')}
                      value={formData.contactId || ''}
                      onChange={(e) => setFormData({ ...formData, contactId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">-- {t('crm.reclamations.selectContact', 'Sélectionner un contact')} --</MenuItem>
                      {contacts.map((c) => (
                        <MenuItem key={c.id || c.CCT_IDF} value={c.id || c.CCT_IDF}>
                          {c.firstName} {c.lastName} ({c.company || 'Contact'})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.reclamations.type', 'Nature de l\'Anomalie')}
                      value={formData.type || 'DEFAUT_POSE'}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as ReclamationType })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="DEFAUT_POSE">{t('crm.reclamations.typeDefautPose', 'Défaut de Pose / Fixation / Calage')}</MenuItem>
                      <MenuItem value="DEFAUT_PRODUIT">{t('crm.reclamations.typeDefautProduit', 'Défaut Produit / Rayure / Vitrage')}</MenuItem>
                      <MenuItem value="RETARD_LIVRAISON">{t('crm.reclamations.typeRetardLivraison', 'Retard de Livraison Chantier')}</MenuItem>
                      <MenuItem value="ACCESSOIRES_MANQUANTS">{t('crm.reclamations.typeAccessoiresManquants', 'Accessoires / Quincaillerie Manquante')}</MenuItem>
                      <MenuItem value="AUTRE">{t('crm.reclamations.typeAutre', 'Autre Réclamation')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.reclamations.severity', 'Niveau de Gravité / Sévérité')}
                      value={formData.severity || 'MEDIUM'}
                      onChange={(e) => setFormData({ ...formData, severity: e.target.value as ReclamationSeverity })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="LOW">{t('crm.reclamations.sevLow', 'Faible (Impact mineur)')}</MenuItem>
                      <MenuItem value="MEDIUM">{t('crm.reclamations.sevMedium', 'Moyenne (À traiter sous 48h)')}</MenuItem>
                      <MenuItem value="HIGH">{t('crm.reclamations.sevHigh', 'Élevée (Bloque la réception)')}</MenuItem>
                      <MenuItem value="CRITICAL">{t('crm.reclamations.sevCritical', 'Critique (Arrêt immédiat de pose)')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('common.status', 'État de Résolution')}
                      value={formData.status || 'OPEN'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as ReclamationStatus })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="OPEN">{t('crm.reclamations.statusOpen', 'Ouverte (En attente)')}</MenuItem>
                      <MenuItem value="INVESTIGATING">{t('crm.reclamations.statusInvestigating', 'En Cours d\'Analyse Usine / SAV')}</MenuItem>
                      <MenuItem value="IN_PROGRESS">{t('crm.reclamations.statusInProgress', 'Intervention Technique Planifiée')}</MenuItem>
                      <MenuItem value="RESOLVED">{t('crm.reclamations.statusResolved', 'Résolue & Réceptionnée')}</MenuItem>
                      <MenuItem value="CLOSED">{t('crm.reclamations.statusClosed', 'Dossier Fermé')}</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Résolution */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ResolvedIcon color="success" /> {t('crm.reclamations.secResolution', 'Traitement & Résolution Technique')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.reclamations.dateReported', 'Date de Signalement')}
                      type="datetime-local"
                      value={formData.dateReported || ''}
                      onChange={(e) => setFormData({ ...formData, dateReported: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.reclamations.dateResolved', 'Date de Résolution')}
                      type="datetime-local"
                      value={formData.dateResolved || ''}
                      onChange={(e) => setFormData({ ...formData, dateResolved: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.reclamations.resolutionNotes', 'Notes de Résolution & Actions Correctives')}
                      value={formData.resolutionNotes || ''}
                      onChange={(e) => setFormData({ ...formData, resolutionNotes: e.target.value })}
                      multiline
                      rows={4}
                      fullWidth
                      placeholder={t('crm.reclamations.resolutionPlaceholder', 'Détaillez les pièces remplacées, l\'intervention du technicien SAV, la levée des réserves...')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.reclamations.title', 'SAV & Réclamations Clients Chantiers')}
      icon={<SavIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {reclamationsList.map((rec) => {
            const recId = rec.id || rec.CRC_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={recId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: rec.severity === 'CRITICAL' ? 'error.main' : 'warning.main', width: 44, height: 44 }}>
                        <AlertIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={rec.subject}>
                          {rec.subject}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rec.code}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(rec.severity, rec.severity)}
                        color={getSeverityColor(rec.severity)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={t(rec.type, rec.type)} size="small" variant="outlined" />
                        <Chip
                          label={t(rec.status, rec.status)}
                          color={getStatusColor(rec.status) as any}
                          size="small"
                        />
                      </Stack>

                      {rec.chantierTitle && (
                        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                          🏢 {rec.chantierTitle}
                        </Typography>
                      )}

                      {rec.clientName && (
                        <Typography variant="body2" color="text.secondary">
                          👤 {t('crm.reclamations.client', 'Client')}: {rec.clientName}
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary">
                        {t('crm.reclamations.reportedOn', 'Signalé le')} : {rec.dateReported ? new Date(rec.dateReported).toLocaleDateString() : '-'}
                      </Typography>
                    </Stack>

                    {rec.resolutionNotes && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1
                        }}
                      >
                        {rec.resolutionNotes}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {rec.dateResolved ? `${t('crm.reclamations.resolvedOn', 'Résolu le')} ${new Date(rec.dateResolved).toLocaleDateString()}` : t('crm.reclamations.notResolved', 'Non résolu')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(rec)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(rec)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 130 },
            { id: 'subject', label: t('crm.reclamations.subject', 'Objet du Litige'), minWidth: 220 },
            { id: 'chantierTitle', label: t('crm.reclamations.chantier', 'Chantier'), width: 200 },
            { id: 'clientName', label: t('crm.reclamations.client', 'Client'), width: 160 },
            { id: 'type', label: t('crm.reclamations.type', 'Type'), width: 160, chip: true },
            {
              id: 'severity',
              label: t('crm.reclamations.severity', 'Sévérité'),
              width: 120,
              chip: true,
              chipColorMap: {
                LOW: 'info',
                MEDIUM: 'warning',
                HIGH: 'error',
                CRITICAL: 'error'
              }
            },
            {
              id: 'status',
              label: t('common.status', 'Statut'),
              width: 140,
              chip: true,
              chipColorMap: {
                OPEN: 'error',
                INVESTIGATING: 'info',
                IN_PROGRESS: 'warning',
                RESOLVED: 'success',
                CLOSED: 'success'
              }
            },
          ]}
          data={reclamationsList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.reclamations.search', 'Rechercher une réclamation, chantier...')}
          filterColumns={['code', 'subject', 'chantierTitle', 'clientName', 'type', 'severity', 'status']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default ReclamationsPage;
